<div class="csv-import-container">
  <div class="import-header">
    <h2>Product Import</h2>
    <p>Import products and categories from a CSV file</p>
    
    <!-- Connection Status -->
    <div class="connection-status" [ngClass]="{'connected': isConnected, 'disconnected': !isConnected}">
      <span class="status-indicator"></span>
      {{ isConnected ? 'Connected' : 'Connecting...' }}
    </div>
  </div>

  <!-- CSV Format Information -->
  <div class="format-info" *ngIf="!selectedFile">
    <h3>CSV Format Requirements</h3>
    <div class="format-details">
      <p><strong>Accepted columns (in this exact order):</strong></p>
      <div class="column-tags">
        <span class="column-badge" *ngFor="let col of csvColumns">{{ col }}</span>
      </div>
      
      <div class="format-notes">
        <h4>Notes:</h4>
        <ul>
          <li>Header row must be present</li>
          <li>Maximum file size: 10MB</li>
          <li>Accepted formats: .csv files only</li>
          <li>Boolean values: TRUE/FALSE</li>
          <li><strong>Category Visible:</strong> optional TRUE/FALSE — controls whether the category is visible to customers. Defaults to TRUE. When re-importing, updates existing category visibility if provided.</li>
          <li>Tax rate format: 25% (with % symbol)</li>
          <li>Cost price: optional numeric field (e.g. 6.50)</li>
          <li>Provider deposit code: optional text field</li>
          <li>Image URL: optional full URL to a product image (e.g. https://example.com/product.jpg) — use the Image Library tab to host images and copy their URL</li>
          <li>Age: optional numeric field — minimum age required to purchase (e.g. 18). Only relevant when Age Restricted is TRUE. If Age Restricted is TRUE and Age is blank or invalid, the minimum age defaults to 16 and a warning is shown in the import results</li>
          <li><strong>Category ID:</strong> optional — link product to an existing category by MongoDB ID instead of name</li>
          <li><strong>DSK columns</strong> (Main Group, Product Group, Sub Group, DSK Assortment ID, Family, Discount Group): optional DSK hierarchy fields</li>
          <li><strong>Unit Code / Unit Content:</strong> optional unit pricing fields (e.g. CL / 75 for centilitres)</li>
        </ul>
      </div>
      
      <button class="btn btn-outline" (click)="downloadTemplate()">
        <i class="fas fa-download"></i> Download Template
      </button>
    </div>
  </div>

  <!-- File Upload Area -->
  <div class="upload-section" *ngIf="!isUploading && !showResults">
    <div 
      class="file-drop-zone" 
      [ngClass]="{'drag-over': isDragOver, 'has-file': selectedFile}"
      (drop)="onFileDrop($event)"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)">
      
      <div *ngIf="!selectedFile" class="drop-zone-content">
        <i class="fas fa-cloud-upload-alt"></i>
        <h3>Drop your CSV file here</h3>
        <p>or</p>
        <input 
          type="file" 
          id="csvFile" 
          accept=".csv" 
          (change)="onFileSelected($event)"
          hidden>
        <label for="csvFile" class="btn btn-primary">Choose File</label>
      </div>

      <div *ngIf="selectedFile" class="selected-file-info">
        <i class="fas fa-file-csv"></i>
        <div class="file-details">
          <h4>{{ selectedFile.name }}</h4>
          <p>{{ getFileSize(selectedFile.size) }}</p>
        </div>
        <button class="btn btn-danger btn-sm" (click)="clearFile()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="upload-actions" *ngIf="selectedFile">
      <button 
        class="btn btn-success" 
        (click)="uploadFile()"
        [disabled]="!isConnected">
        <i class="fas fa-upload"></i>
        Start Import
      </button>
      <button class="btn btn-secondary" (click)="clearFile()">
        <i class="fas fa-times"></i>
        Cancel
      </button>
    </div>
  </div>

  <!-- Progress Section -->
  <div class="progress-section" *ngIf="isUploading && importStatus">
    <div class="progress-header">
      <h3>Importing Products...</h3>
      <span class="progress-stats">
        {{ importStatus.processedRows || 0 }} / {{ importStatus.totalRows || 0 }} rows
      </span>
    </div>

    <div class="progress-bar-container">
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          [style.width.%]="progressPercentage">
        </div>
      </div>
      <span class="progress-percentage">{{ progressPercentage }}%</span>
    </div>

    <div class="progress-details" *ngIf="importStatus.currentStage">
      <p class="current-stage">{{ importStatus.currentStage }}</p>
      <p class="time-remaining" *ngIf="estimatedTimeRemaining">{{ estimatedTimeRemaining }}</p>
    </div>

    <div class="progress-stats-grid">
      <div class="stat-item">
        <span class="stat-value">{{ importStatus.successfulImports || 0 }}</span>
        <span class="stat-label">Successful</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">{{ importStatus.errors.length || 0 }}</span>
        <span class="stat-label">Errors</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">{{ importStatus.warnings.length || 0 }}</span>
        <span class="stat-label">Warnings</span>
      </div>
    </div>
  </div>

  <!-- Results Section -->
  <div class="results-section" *ngIf="showResults && importStatus">
    <div class="results-header" [ngClass]="importStatus.status">
      <i class="fas" [ngClass]="{
        'fa-check-circle': importStatus.status === 'completed',
        'fa-exclamation-circle': importStatus.status === 'error'
      }"></i>
      <h3>
        {{ importStatus.status === 'completed' ? 'Import Completed' : 'Import Failed' }}
      </h3>
    </div>

    <!-- Success Results -->
    <div class="success-results" *ngIf="importStatus.status === 'completed'">
      <div class="results-summary">
        <div class="summary-grid">
          <div class="summary-item">
            <span class="value">{{ importStatus.totalRows }}</span>
            <span class="label">Total Rows</span>
          </div>
          <div class="summary-item">
            <span class="value">{{ importStatus.successfulImports }}</span>
            <span class="label">Successful</span>
          </div>
          <div class="summary-item">
            <span class="value">{{ importStatus.newProducts.length || 0 }}</span>
            <span class="label">New Products</span>
          </div>
          <div class="summary-item">
            <span class="value">{{ importStatus.newCategories.length || 0 }}</span>
            <span class="label">New Categories</span>
          </div>
          <div class="summary-item">
            <span class="value">{{ importStatus.duration }}s</span>
            <span class="label">Duration</span>
          </div>
        </div>
      </div>

      <!-- New Categories -->
      <div class="new-categories" *ngIf="importStatus.newCategories && importStatus.newCategories.length > 0">
        <h4>New Categories Created</h4>
        <div class="category-list">
          <span class="category-tag" *ngFor="let category of importStatus.newCategories">
            {{ category }}
          </span>
        </div>
      </div>

      <!-- Warnings -->
      <div class="warnings-section" *ngIf="importStatus.warnings && importStatus.warnings.length > 0">
        <h4>Warnings ({{ importStatus.warnings.length }})</h4>
        <div class="message-list warnings">
          <div class="message-item" *ngFor="let warning of importStatus.warnings">
            <span class="row-number">Row {{ warning.row }}</span>
            <span class="product-name">{{ warning.productName }}</span>
            <span class="message">{{ warning.warning }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Results -->
    <div class="error-results" *ngIf="importStatus.status === 'error'">
      <div class="error-message">
        <p>The import process failed. Please check the file format and try again.</p>
      </div>
    </div>

    <!-- Errors -->
    <div class="errors-section" *ngIf="importStatus.errors && importStatus.errors.length > 0">
      <h4>Errors ({{ importStatus.errors.length }})</h4>
      <div class="message-list errors">
        <div class="message-item" *ngFor="let error of importStatus.errors">
          <span class="row-number">Row {{ error.row }}</span>
          <span class="product-name">{{ error.productName }}</span>
          <span class="message">{{ error.error }}</span>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="results-actions">
      <button class="btn btn-primary" (click)="clearFile(); showResults = false;">
        <i class="fas fa-plus"></i>
        Import Another File
      </button>
      <button class="btn btn-secondary" *ngIf="importStatus.status === 'error'" (click)="retry()">
        <i class="fas fa-redo"></i>
        Retry
      </button>
    </div>
  </div>
</div>
